import type { ReactNode } from "react";

import { ApiError } from "@/api/client";
import { queryKeys, useSessionQuery } from "@/api/hooks";
import { isServerAnsweredFailure } from "@/api/queryClient";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { SessionProvider } from "@/auth/SessionContext";
import type { AuthAdapter } from "@/auth/types";
import { useRuntimeConfig } from "@/config/RuntimeConfigContext";
import { Button } from "@/ui/Button";

import { FailClosedScreen, RetryButton } from "./FailClosedScreen";

/**
 * Resolves the caller's session before any application chrome renders.
 *
 * An authenticated user with no authorization is not shown a partially usable
 * editor: the shell refuses to mount, which is what "fails closed" means in
 * issue #4's acceptance criteria.
 */
export function SessionGate({
  adapter,
  children,
}: {
  adapter: AuthAdapter;
  children: ReactNode;
}) {
  const session = useSessionQuery();
  const config = useRuntimeConfig();
  // Not `session.refetch()`: on a paused query that has never succeeded that
  // is a silent no-op, and this screen is the whole application (#215).
  const retrySession = useRetryPausedQuery(queryKeys.session);

  if (session.isPending) {
    // A paused session query has no request in flight — the retry is being
    // held. Before #215 this branch showed "Loading your workspace…" forever,
    // with nothing on screen admitting it and no way to recover.
    if (session.isPaused) {
      // Held for which reason, though? Two independent clauses park a retry —
      // no connection, or a hidden document — and #327 is what it costs to
      // read the second as the first. This branch does not route through
      // `QueryPendingState`, so it takes the same predicate directly; the
      // blast radius here is the whole application shell rather than one card.
      //
      // An `ApiError` exists only where a response did, so this is the Editor
      // Service refusing the session, not the browser failing to reach it. It
      // gets the same screen the settled failure below gets, with the status
      // it actually returned — the one fact that makes "offline" checkable
      // rather than a guess.
      const failure = session.failureReason;
      if (isServerAnsweredFailure(failure)) {
        return (
          <FailClosedScreen
            title="The Editor Service is unavailable"
            summary="Your session could not be established: the Editor Service answered this request with a failure rather than a session. The status it returned is below."
            details={[
              `HTTP ${failure.status} · ${failure.code}`,
              ...(failure.requestId ? [`Request ID: ${failure.requestId}`] : []),
            ]}
            // Not `session.refetch()`, for the reason recorded above the
            // `retrySession` declaration: this query is paused, and paused is
            // exactly the state a bare refetch cannot leave.
            actions={<RetryButton onRetry={retrySession} />}
          />
        );
      }

      return (
        <FailClosedScreen
          title="You appear to be offline"
          summary="Your workspace could not be loaded because the Editor Service could not be reached. It loads on its own when the connection returns."
          actions={<RetryButton onRetry={retrySession} />}
        />
      );
    }

    return (
      <div
        role="status"
        aria-live="polite"
        className="flex min-h-screen items-center justify-center bg-background"
      >
        <p className="text-sm text-muted-foreground">Loading your workspace…</p>
      </div>
    );
  }

  if (session.isError) {
    const error = session.error;
    const isUnauthenticated =
      error instanceof ApiError && error.isUnauthenticated;
    const isForbidden = error instanceof ApiError && error.isForbidden;

    // The adapter owns post-sign-out navigation; navigating here would race the
    // provider's end-session redirect. Where there is no session to end — the
    // development stub — the control is disabled with the reason rather than
    // offered and inert.
    const signOut = (
      <Button
        variant="outline"
        disabled={!adapter.supportsSignOut}
        title={
          adapter.supportsSignOut
            ? undefined
            : "The local development identity has no session to end."
        }
        onClick={() => {
          void adapter.signOut();
        }}
      >
        Sign out
      </Button>
    );

    // 401 and 403 are different situations with different recoveries (#44).
    // Unauthenticated means the sign-in is gone — expired past silent renewal,
    // or ended elsewhere — and the one action that helps is a new interactive
    // sign-in. Forbidden means the identity is fine but this environment
    // refuses it; signing in again cannot help, and the actions are retrying
    // (authorization is resolved per request) or signing out to switch.
    if (isUnauthenticated) {
      return (
        <FailClosedScreen
          title="Your session has expired"
          summary="Your sign-in is no longer valid — it expired, or it was signed out elsewhere. Sign in again to continue."
          details={
            error instanceof ApiError
              ? [
                  `HTTP ${error.status} · ${error.code}`,
                  ...(error.requestId ? [`Request ID: ${error.requestId}`] : []),
                ]
              : []
          }
          actions={
            <>
              <Button
                variant="primary"
                onClick={() => {
                  void adapter.signIn();
                }}
              >
                Sign in again
              </Button>
              {signOut}
            </>
          }
        />
      );
    }

    return (
      <FailClosedScreen
        title={isForbidden ? "Access denied" : "The Editor Service is unavailable"}
        summary={
          isForbidden
            ? "Your account is signed in but is not authorized to use the Clinician Editor in this environment."
            : "Your session could not be established because the Editor Service did not respond."
        }
        details={
          error instanceof ApiError
            ? [
                `HTTP ${error.status} · ${error.code}`,
                ...(error.requestId ? [`Request ID: ${error.requestId}`] : []),
              ]
            : []
        }
        actions={
          <>
            <RetryButton onRetry={() => void session.refetch()} />
            {signOut}
          </>
        }
        footer="Ask a workspace administrator to grant access, quoting the request ID above."
      />
    );
  }

  // `/config.json` and the session must name the same environment (#24).
  //
  // This is NOT about which value the badge shows — that was ruled (#18,
  // 2026-08-02): the badge renders the SESSION's environment, because the
  // deployment identity is the server's to state. This is about the fact that
  // `config.environment` has already been used, before this component ever
  // ran, to make two security decisions in config/schema.ts:
  //
  //   * the `dev-stub` identity is admitted only when it reads "local";
  //   * the mock Editor Service is admitted only when it reads "local".
  //
  // So a config.json claiming `local` while the service answering it is
  // `staging` does not merely mislabel a badge — it boots a real staging
  // session with the development identity enabled. The badge cannot catch
  // that, precisely because the badge now reads the session and would
  // cheerfully render "staging" over an app that admitted a stub.
  //
  // Refusing to mount is the same choice this gate already makes for an
  // unauthorized session, and for the same reason: the disagreement cannot be
  // repaired in the browser — one of the two artifacts was deployed wrong, and
  // only a redeploy fixes it.
  if (session.data.environment !== config.environment) {
    return (
      <FailClosedScreen
        title="This deployment is inconsistent"
        summary={
          "The Editor Service and this web app disagree about which environment they belong to. " +
          "Local-only identity and mock-service permissions are decided from the web app's " +
          "configuration, so this session cannot be trusted."
        }
        details={[
          `Editor Service reports: ${session.data.environment}`,
          `This web app is configured as: ${config.environment}`,
        ]}
        actions={<RetryButton onRetry={() => void session.refetch()} />}
        footer="Report this to DevOps: one of the two was deployed with the wrong environment configuration."
      />
    );
  }

  return (
    <SessionProvider session={session.data} adapter={adapter}>
      {children}
    </SessionProvider>
  );
}
